(() => {
  const YMQ_TIMEOUT = 15000;

  const guards = new WeakMap();
  const activeGuards = new Set();

  const isTestFailureMode = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ymq_test') === 'fail';
  };

  const ymqIsReady = (guard) => {
    if (isTestFailureMode()) {
      return false;
    }

    const ymq = guard.querySelector('ymq-option');

    if (!ymq) {
      return false;
    }

    const optionBox = ymq.querySelector(
      '.ymq-options-box, [ymq-option-id]'
    );

    const optionControl = ymq.querySelector(
      'select, input:not([type="hidden"]), textarea, button, [role="radio"], [role="checkbox"]'
    );

    return Boolean(optionBox && optionControl);
  };

  const getState = (guard) => {
    return guards.get(guard);
  };

  const guardIsLocked = (guard) => {
    const state = getState(guard);

    if (!state) {
      return false;
    }

    return !state.ready || !ymqIsReady(guard);
  };

  const anyGuardIsLocked = () => {
    for (const guard of activeGuards) {
      if (guardIsLocked(guard)) {
        return true;
      }
    }

    return false;
  };

  const lockButton = (state) => {
    if (!state || !state.button) {
      return;
    }

    state.button.disabled = true;
    state.button.setAttribute('aria-disabled', 'true');
  };

  const showLoading = (state) => {
    if (!state) {
      return;
    }

    state.ready = false;
    state.failed = false;

    lockButton(state);

    if (state.buttonText) {
      state.buttonText.textContent = 'LOADING OPTIONS...';
    }

    if (state.loadingMessage) {
      state.loadingMessage.hidden = false;
    }

    if (state.errorMessage) {
      state.errorMessage.hidden = true;
    }

    state.guard.dataset.ymqFailsafeState = 'loading';
  };

  const showReady = (state) => {
    if (!state || state.ready) {
      return;
    }

    state.ready = true;
    state.failed = false;

    window.clearTimeout(state.timeoutId);

    state.button.disabled = false;
    state.button.removeAttribute('aria-disabled');

    if (state.buttonText) {
      state.buttonText.textContent = 'ADD TO CART';
    }

    if (state.loadingMessage) {
      state.loadingMessage.hidden = true;
    }

    if (state.errorMessage) {
      state.errorMessage.hidden = true;
    }

    state.guard.dataset.ymqFailsafeState = 'ready';
  };

  const showFailure = (state) => {
    if (!state) {
      return;
    }

    state.ready = false;
    state.failed = true;

    lockButton(state);

    if (state.buttonText) {
      state.buttonText.textContent = 'OPTIONS UNAVAILABLE';
    }

    if (state.loadingMessage) {
      state.loadingMessage.hidden = true;
    }

    if (state.errorMessage) {
      state.errorMessage.hidden = false;
    }

    state.guard.dataset.ymqFailsafeState = 'failed';
  };

  const checkYmq = (state) => {
    if (!state) {
      return;
    }

    if (ymqIsReady(state.guard)) {
      showReady(state);
    } else {
      lockButton(state);
    }
  };

  const blockEvent = (event, guard) => {
    const state = getState(guard);

    if (!state) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    lockButton(state);
  };

  const initialiseGuard = (guard) => {
    if (guard.dataset.ymqFailsafeInitialised === 'true') {
      return;
    }

    const button = guard.querySelector(
      '[data-ymq-failsafe-button]'
    );

    const buttonText = guard.querySelector(
      '[data-ymq-failsafe-button-text]'
    );

    const loadingMessage = guard.querySelector(
      '[data-ymq-failsafe-loading]'
    );

    const errorMessage = guard.querySelector(
      '[data-ymq-failsafe-error]'
    );

    const form = guard.closest('form');

    if (!button || !buttonText || !form) {
      return;
    }

    const state = {
      guard,
      form,
      button,
      buttonText,
      loadingMessage,
      errorMessage,
      ready: false,
      failed: false,
      timeoutId: null,
      observer: null
    };

    guards.set(guard, state);
    activeGuards.add(guard);

    guard.dataset.ymqFailsafeInitialised = 'true';

    showLoading(state);

    state.observer = new MutationObserver(() => {
      checkYmq(state);
    });

    state.observer.observe(guard, {
      childList: true,
      subtree: true
    });

    checkYmq(state);

    state.timeoutId = window.setTimeout(() => {
      if (!ymqIsReady(guard)) {
        showFailure(state);
      }
    }, YMQ_TIMEOUT);
  };

  const initialiseAllGuards = () => {
    document
      .querySelectorAll('[data-ymq-failsafe]')
      .forEach(initialiseGuard);
  };

  /*
   * CLICK PROTECTION
   *
   * Blocks Add to Cart-style interactions while
   * the YMQ fail-safe is locked.
   *
   * YMQ option controls remain usable.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const guard = target.closest('[data-ymq-failsafe]');

      if (!guard) {
        return;
      }

      if (target.closest('ymq-option')) {
        return;
      }

      if (guardIsLocked(guard)) {
        blockEvent(event, guard);
      }
    },
    true
  );

  /*
   * FORM SUBMISSION PROTECTION
   */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const guard = form.querySelector(
        '[data-ymq-failsafe]'
      );

      if (!guard) {
        return;
      }

      if (guardIsLocked(guard)) {
        blockEvent(event, guard);
      }
    },
    true
  );

  /*
   * NETWORK PROTECTION
   *
   * YMQ may add products directly through JavaScript,
   * so requests to Shopify's cart/add endpoint are also
   * blocked while the fail-safe is locked.
   */
  const isCartAddRequest = (url) => {
    if (!url) {
      return false;
    }

    try {
      const parsedUrl = new URL(
        url,
        window.location.origin
      );

      return /\/cart\/add(?:\.js)?\/?$/.test(
        parsedUrl.pathname
      );
    } catch (error) {
      return false;
    }
  };

  /*
   * FETCH PROTECTION
   */
  const originalFetch = window.fetch;

  if (typeof originalFetch === 'function') {
    window.fetch = function (...args) {
      const request = args[0];

      let url = '';

      if (typeof request === 'string') {
        url = request;
      } else if (
        request &&
        typeof request.url === 'string'
      ) {
        url = request.url;
      }

      if (
        isCartAddRequest(url) &&
        anyGuardIsLocked()
      ) {
        console.warn(
          '[YMQ Fail-safe] Blocked cart/add request because product options are not ready.'
        );

        return Promise.reject(
          new Error(
            'YMQ product options are not ready.'
          )
        );
      }

      return originalFetch.apply(this, args);
    };
  }

  /*
   * XMLHTTPREQUEST PROTECTION
   */
  const originalXhrOpen =
    XMLHttpRequest.prototype.open;

  const originalXhrSend =
    XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...rest
  ) {
    this._ymqFailsafeUrl = url;
    this._ymqFailsafeMethod = method;

    return originalXhrOpen.call(
      this,
      method,
      url,
      ...rest
    );
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (
      isCartAddRequest(this._ymqFailsafeUrl) &&
      anyGuardIsLocked()
    ) {
      console.warn(
        '[YMQ Fail-safe] Blocked XMLHttpRequest cart/add request because product options are not ready.'
      );

      try {
        this.abort();
      } catch (error) {
        // Nothing else is required.
      }

      return;
    }

    return originalXhrSend.apply(this, args);
  };

  /*
   * INITIAL PAGE LOAD
   */
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAllGuards
    );
  } else {
    initialiseAllGuards();
  }

  /*
   * SHOPIFY SECTION RELOADS
   */
  document.addEventListener(
    'shopify:section:load',
    initialiseAllGuards
  );
})();